/* assets/gcitypartners-gtender-opportunity-bridge.js — GCITYPARTNERS-GTENDER-BRIDGE-P0
 * Child projection of the existing #759 GTender -> GCityPartners handoff.
 * Revalidates tender context against canonical public detail before prefilling supplier search.
 * Does not authenticate, save GTender state, create projects/RFQs, select suppliers or certify eligibility.
 */
(function (window, document) {
  'use strict';

  var STORAGE_KEY = 'gcp_dashboard_brief_handoff_v1';
  var ROOT_SELECTOR = '[data-gcp-supplier-network]';
  var DETAIL_ENDPOINT = 'https://api.glhksolution.com/api/tender/detail';
  var MAX_AGE_MS = 30 * 60 * 1000;
  var TENDER_ID_RE = /^[A-Za-z0-9._:-]{3,240}$/;
  const mounted = new WeakSet();
  /**
   * @typedef {{
   *   id: string,
   *   title: string,
   *   reference: string,
   *   detail_url: string,
   *   official_url: string,
   *   created_at: number
   * }} BridgeTenderHandoff
   */
  /**
   * @typedef {Readonly<{
   *   mountAll: (scope?: ParentNode | null) => void,
   *   readHandoff: () => BridgeTenderHandoff | null,
   *   uniqueCapabilities: (value: unknown) => string[],
   *   normalizeCapability: (value: unknown) => string
   * }>} BridgePresentation
   */
  /** @type {Window & {GCityPartnersGTenderOpportunityBridge?: BridgePresentation}} */
  const bridgeWindow = window;
  const existingBridge = bridgeWindow.GCityPartnersGTenderOpportunityBridge;

  if (existingBridge && typeof existingBridge.mountAll === 'function') {
    existingBridge.mountAll(document);
    return;
  }

  /**
   * @param {unknown} value
   * @returns {value is Record<string, unknown>}
   */
  function isBridgeTenderRecord(value) {
    return value !== null && typeof value === 'object' && !Array.isArray(value);
  }
  /** @param {unknown} value */
  function clean(value) {
    return String(value == null ? '' : value).trim();
  }
  function isZh() {
    return /^zh/i.test(clean(document.documentElement.lang));
  }
  /**
   * @param {string} zh
   * @param {string} en
   */
  function words(zh, en) {
    return isZh() ? zh : en;
  }
  /** @param {unknown} value */
  function normalizeCapability(value) {
    return clean(value)
      .toLowerCase()
      .replace(/&/g, ' and ')
      .replace(/[^a-z0-9\u3400-\u9fff]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 80);
  }
  /**
   * @param {unknown} value
   * @returns {string[]}
   */
  function uniqueCapabilities(value) {
    if (!Array.isArray(value)) return [];
    /** @type {unknown[]} */
    const entries = value;
    /** @type {Set<string>} */
    const seen = new Set();

    return entries
      .filter(function (item) {
        return typeof item === 'string' && item.trim().length > 0;
      })
      .map(normalizeCapability)
      .filter(function (id) {
        if (!id || seen.has(id)) return false;
        seen.add(id);
        return true;
      })
      .slice(0, 12);
  }
  function readHandoff() {
    var raw = '';
    try {
      raw = window.sessionStorage.getItem(STORAGE_KEY) || '';
    } catch (_error) {
      return null;
    }
    if (!raw) return null;
    try {
      var value = JSON.parse(raw);
      if (!value || value.version !== 1 || clean(value.source) !== 'gtender') return null;
      var createdAt = Number(value.created_at || 0);
      if (
        !Number.isFinite(createdAt) ||
        createdAt <= 0 ||
        Date.now() - createdAt > MAX_AGE_MS ||
        createdAt - Date.now() > 60000
      )
        return null;
      var context =
        value.tender_context && typeof value.tender_context === 'object'
          ? value.tender_context
          : {};
      var id = clean(context.id);
      if (!TENDER_ID_RE.test(id)) return null;
      return {
        id: id,
        title: clean(context.title).slice(0, 500),
        reference: clean(context.reference).slice(0, 240),
        detail_url: clean(context.detail_url).slice(0, 1000),
        official_url: clean(context.official_url).slice(0, 1000),
        created_at: createdAt,
      };
    } catch (_error) {
      return null;
    }
  }
  /**
   * @param {unknown} payload
   * @returns {Record<string, unknown> | null}
   */
  function detailItem(payload) {
    if (!isBridgeTenderRecord(payload)) return null;
    const data = isBridgeTenderRecord(payload.data) ? payload.data : null;
    var candidates = [
      data && data.item,
      data && data.result,
      data && data.tender,
      data,
      payload.item,
      payload.result,
      payload.tender,
    ];
    for (var index = 0; index < candidates.length; index += 1) {
      var item = candidates[index];
      if (isBridgeTenderRecord(item)) return item;
    }
    return null;
  }
  /** @param {string} id */
  async function refreshTender(id) {
    var url = new URL(DETAIL_ENDPOINT);
    url.searchParams.set('id', id);
    var response = await fetch(url.toString(), {
      method: 'GET',
      credentials: 'omit',
      headers: { Accept: 'application/json' },
    });
    var payload = await response.json().catch(function () {
      return {};
    });
    if (!response.ok || payload.ok === false) throw new Error('tender_detail_unavailable');
    var item = detailItem(payload);
    if (!item) throw new Error('tender_detail_unavailable');
    const returnedId = clean(item.id || item.tender_id);
    if (!TENDER_ID_RE.test(returnedId) || returnedId !== id) {
      throw new Error('tender_detail_mismatch');
    }
    return item;
  }
  /**
   * @template {keyof HTMLElementTagNameMap} T
   * @param {T} tag
   * @param {string} [className]
   * @param {unknown} [text]
   * @returns {HTMLElementTagNameMap[T]}
   */
  function make(tag, className, text) {
    var node = document.createElement(tag);
    if (className) node.className = className;
    if (text != null) node.textContent = String(text);
    return node;
  }
  /**
   * @param {Element} root
   * @param {BridgeTenderHandoff} handoff
   * @param {Record<string, unknown>} item
   * @param {string[]} capabilities
   */
  function render(root, handoff, item, capabilities) {
    root.querySelectorAll('[data-gcp-gtender-opportunity]').forEach(function (panel) {
      panel.remove();
    });

    var panel = make('section', 'gcp-supplier-network__panel');
    panel.setAttribute('data-gcp-gtender-opportunity', '');
    panel.setAttribute('data-tender-id', handoff.id);

    var head = make('div', 'gcp-supplier-network__panel-head');
    var titleWrap = make('div');
    titleWrap.appendChild(make('span', '', 'GT'));
    titleWrap.appendChild(make('h3', '', words('招標與合作方', 'Tender and partners')));
    head.appendChild(titleWrap);
    head.appendChild(make('small', '', words('已重新核對招標詳情', 'Tender details rechecked')));
    panel.appendChild(head);

    panel.appendChild(
      make(
        'strong',
        '',
        clean(item.title || item.subject_zh_hk || item.subject_en || handoff.title) ||
          words('招標機會', 'Tender opportunity')
      )
    );
    var meta = make('div', 'gcp-supplier-network__meta');
    var reference = clean(item.tender_reference || item.reference || handoff.reference);
    var department = clean(
      item.issuing_department_zh_hk ||
        item.issuing_department_en ||
        item.organization ||
        item.issuer
    );
    var deadline = clean(item.closing_datetime_raw || item.deadline);
    [reference, department, deadline].forEach(function (value) {
      if (value) meta.appendChild(make('span', '', value));
    });
    if (meta.childNodes.length) panel.appendChild(meta);

    const copy = capabilities.length
      ? words(
          '搜尋條件來自招標資料中的需求能力：',
          'Search criteria from the tender’s required capabilities: '
        ) + capabilities.join(' · ')
      : words(
          '招標資料未提供可用的需求能力。請先確認官方要求，再在下方輸入合作方搜尋條件。',
          'The tender data provides no usable required capabilities. Check the official requirements, then enter partner search criteria below.'
        );
    panel.appendChild(make('p', 'gcp-supplier-network__fine', copy));

    var actions = make('div', 'gcp-supplier-network__actions');
    if (capabilities.length) {
      const use = make(
        'button',
        'gcp-dashboard__button gcp-dashboard__button--primary',
        words('按需求能力找合作方', 'Find partners by required capability')
      );
      use.type = 'button';
      use.setAttribute('data-primary', 'true');

      use.addEventListener('click', function () {
        const form = root.querySelector('[data-gcp-supplier-search-form]');
        if (!(form instanceof HTMLFormElement)) return;
        const input = form.elements.namedItem('capabilities');
        if (
          !(input instanceof HTMLInputElement) &&
          !(input instanceof HTMLTextAreaElement) &&
          !(input instanceof HTMLSelectElement)
        )
          return;

        const disclosure = form.closest('details');
        if (disclosure) disclosure.open = true;

        input.value = capabilities.join(', ');
        input.dispatchEvent(new Event('input', { bubbles: true }));
        input.focus();

        if (typeof form.requestSubmit === 'function') {
          form.requestSubmit();
        } else if (form.reportValidity()) {
          form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
        }
      });
      actions.appendChild(use);
    }
    var detailUrl = clean(handoff.detail_url);
    if (detailUrl) {
      try {
        var detail = new URL(detailUrl, window.location.origin);
        if (detail.origin === window.location.origin) {
          var link = make(
            'a',
            'gcp-dashboard__button',
            words('返回 GTender 詳情', 'Open GTender detail')
          );
          link.href = detail.toString();
          actions.appendChild(link);
        }
      } catch (_error) {}
    }
    panel.appendChild(actions);
    panel.appendChild(
      make(
        'p',
        'gcp-supplier-network__fine',
        words(
          '能力匹配只用於尋找可能的合作方；不代表投標資格、合作夥伴資格或中標機率。建立 RFQ 仍需你選擇自己的 Professional Project 和合作夥伴。',
          'Capability matching only helps find potential partners; it does not establish bid eligibility, supplier qualification or award probability. Creating an RFQ still requires your own Professional Project and an explicit supplier selection.'
        )
      )
    );

    var layout = root.querySelector('.gcp-supplier-network__layout');
    root.insertBefore(panel, layout || root.firstChild);
  }
  /** @param {Element | null} root */
  async function mount(root) {
    if (!root || mounted.has(root)) return;
    mounted.add(root);
    var handoff = readHandoff();
    if (!handoff) return;
    root.setAttribute('data-gcp-gtender-bridge-state', 'refreshing');
    try {
      var item = await refreshTender(handoff.id);
      var capabilities = uniqueCapabilities(item.required_capabilities);
      render(root, handoff, item, capabilities);
      root.setAttribute(
        'data-gcp-gtender-bridge-state',
        capabilities.length ? 'ready' : 'no-capabilities'
      );
    } catch (_error) {
      root.setAttribute('data-gcp-gtender-bridge-state', 'unavailable');
    }
  }
  /** @param {ParentNode | null} [scope] */
  function mountAll(scope) {
    (scope || document).querySelectorAll(ROOT_SELECTOR).forEach(function (root) {
      void mount(root);
    });
  }

  bridgeWindow.GCityPartnersGTenderOpportunityBridge = Object.freeze({
    mountAll: mountAll,
    readHandoff: readHandoff,
    uniqueCapabilities: uniqueCapabilities,
    normalizeCapability: normalizeCapability,
  });
  document.addEventListener('shopify:section:load', function (event) {
    mountAll(event.target instanceof Element ? event.target : document);
  });
  if (document.readyState === 'loading')
    document.addEventListener(
      'DOMContentLoaded',
      function () {
        mountAll(document);
      },
      { once: true }
    );
  else mountAll(document);
})(window, document);
